import PopupBasic from '../components/popups/PopupBasic';
import uiConfig from 'src/lib/ui/config';
import { Feature } from 'src/replicant/analytics';
import {
  ImageCoverView,
  ImageScaleView,
  ImageView,
  View,
} from '@play-co/timestep-core/ui';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import { FlexBoxView } from '@play-co/timestep-core/flexbox';
import { DecorationItem } from 'src/replicant/decoration/decoration.ruleset';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import { getDecorationItemPath } from './helpers';
import StateObserver from 'src/StateObserver';
import { assertNever } from 'src/replicant/utils';
import * as Inventory from 'src/replicant/inventory';
import { trackPinchEvent } from 'src/lib/analytics/events';
import {
  hasAlternativeWaysToGetGems,
  startGemsPinchSequence,
} from '../shop/Helpers';

import { AssetGroup } from '@play-co/timestep-core';

// Only gems for now, for coins we need do extra work with UI
// There could be mixed coins and gems and this will require show this somehow
// Also, mixed currency price will required more advanced pinch sequence
type SupportedCurrency = 'gems'; // bPoints

/**
 * Confirmation popup before purchasing decoration items
 */
export default class PopupDecorationPurchaseConfirmation extends PopupBasic {
  private close: (result: boolean) => void;
  private items: FlexBoxView;
  private setFullPrice: (currency: SupportedCurrency, price: number) => void;
  private setBalanceAfter: (currency: SupportedCurrency, price: number) => void;
  private okButton: ButtonScaleViewWithText;
  private bottomLayout: View;
  private data!: DecorationItem[];

  constructor(opts: { superview: View; close: (result: boolean) => void }) {
    super({
      ...opts,
      superview: opts.superview,
      close: () => opts.close(false),
      width: uiConfig.popups.buruburu.width,
      height: 776,
      darkerBg: true,
      tracking: {
        feature: Feature.DECORATION._,
        $subFeature: Feature.DECORATION.PURCHASE_CONFIRMATION_POPUP,
      },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    // Update message position
    this.message.updateOpts({
      autoSize: false,
      size: 25,
      x: 0,
      y: 71,
      width: uiConfig.popups.buruburu.width,
      height: 32,
      color: '#BF8147',
    });

    // Separate line
    this.createSeparatorLine(this.box, {
      y: 114,
      width: 487,
      height: 5,
      color: '#FDC381',
    });

    // Items container
    this.items = new FlexBoxView({
      superview: this.box,
      width: 510,
      height: 338,
      x: (this.box.style.width - 510) * 0.5,
      y: 138,
      flex: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        alignContent: 'flex-start',
      },
    });

    this.bottomLayout = new View({
      superview: this.box,
      y: 500,
      width: this.box.style.width,
      height: 500,
    });

    // Separate line
    this.createSeparatorLine(this.bottomLayout, {
      y: 0,
      width: 487,
      height: 5,
      color: '#FDC381',
    });

    // Labels
    this.setFullPrice = this.createLabel(this.bottomLayout, {
      text: i18n('decoration.confirmation.requiredGems'),
      y: 27,
      width: 481,
    });

    this.setBalanceAfter = this.createLabel(this.bottomLayout, {
      text: i18n('decoration.confirmation.balanceAfterPurchase'),
      y: 90,
      width: 481,
    });

    // Buttons section
    const { ok } = this.createMainControls(this.bottomLayout, { y: 160 });

    this.okButton = ok;
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/decoration/frames/rectangle.png',
      'assets/ui/decoration/frames/price.png',
      'assets/ui/decoration/frames/rectangle_light.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/hud/icons/hud_coin.png',
    ]);
  }

  /**
   * On dialog show
   */
  public init(opts: { items: DecorationItem[] }) {
    super.init({
      title: i18n('decoration.confirmation.title'),
      message: i18n('decoration.confirmation.message'),
    });

    const { items } = opts;
    this.data = items;

    this.items.removeAllSubviews();

    this.dynamicSize(items.length);

    items.forEach((item) => {
      this.createItemView(this.items, item);
    });

    // Update total price
    this.updateTotal(items);
  }

  /**
   * User pressed ok button
   */
  private async onConfirm() {
    sounds.sfx.play('ok');

    // Calculate full price
    const fullGemsPrice = this.calculateItemsSum('gems', this.data);

    // Purchase flow
    if (this.hasEnoughGems(fullGemsPrice)) return this.close(true);

    // Pinch analytics
    trackPinchEvent({
      resource: 'gems',
      trigger: 'decoration',
    });

    // Show gems shop
    await startGemsPinchSequence({
      feature: 'decoration',
      crossplayTracking: {
        feature: 'decoration',
        subFeature: 'decoration_buy',
      },
    });

    // Recalculate total
    this.updateTotal(this.data);
  }

  /**
   * Update total price
   */
  private updateTotal(items: DecorationItem[]) {
    const fullGemsPrice = this.calculateItemsSum('gems', items);
    const gemsBalanceAfter = this.balanceAfter('gems', fullGemsPrice);

    this.setFullPrice('gems', fullGemsPrice);
    this.setBalanceAfter('gems', gemsBalanceAfter);

    // Has enough gems, keep button on
    if (this.hasEnoughGems(fullGemsPrice)) {
      this.okButton.setDisabled(false);
      return;
    }

    // In rare case when user don't have other ways to get gems
    // and IAP disabled we should turn off OK button to just avoid bad UX
    if (hasAlternativeWaysToGetGems()) {
      // We could show some promo
      this.okButton.setDisabled(false);
    } else {
      // There is not anything to show to this user
      this.okButton.setDisabled(true);
    }
  }

  /**
   * Check if we has has enough gems
   */
  private hasEnoughGems(gemsRequired: number): boolean {
    const { user } = StateObserver.getState();
    const gems = Inventory.getters.getGems(user);

    return gems >= gemsRequired;
  }

  /**
   * Depend on items change dialog size
   */
  private dynamicSize(count: number) {
    switch (count) {
      case 1:
      case 2:
      case 3:
      case 4:
        this.items.style.width = 510;
        this.items.style.x = (this.box.style.width - 510) * 0.5;
        this.box.updateOpts({
          height: 600,
        });
        this.bottomLayout.updateOpts({ y: 330 });
        break;
      case 5:
      case 6:
        this.items.style.width = 410;
        this.items.style.x = (this.box.style.width - 410) * 0.5;
        this.root.updateOpts({ height: 776 });
        this.bottomLayout.updateOpts({ y: 508 });
        break;
      default:
        this.items.style.width = 510;
        this.items.style.x = (this.box.style.width - 510) * 0.5;
        this.root.updateOpts({ height: 776 });
        this.bottomLayout.updateOpts({ y: 508 });
        break;
    }
  }

  /**
   * Calculate sum price of items
   */
  private calculateItemsSum(
    currency: SupportedCurrency,
    items: DecorationItem[],
  ): number {
    return items
      .filter((item) => item.currency === currency)
      .reduce((acc, item) => {
        return item.price + acc;
      }, 0);
  }

  /**
   * Balance after
   */
  private balanceAfter(currency: SupportedCurrency, fullPrice: number): number {
    const { user } = StateObserver.getState();

    switch (currency) {
      case 'gems':
        return Inventory.getters.getGems(user) - fullPrice;
      // case 'bPoints':
      //   return Inventory.getters.getPoints(user) - fullPrice;
      default:
        assertNever(currency);
    }
  }

  /**
   * Create one item view
   */
  private createItemView(superview: View, item: DecorationItem): View {
    const view = new FlexBoxView({
      tag: 'item',
      superview,
      width: 117,
      height: 163,
      flex: {
        flexDirection: 'column',
        flexWrap: 'no-wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        alignContent: 'center',
        margin: 5,
        marginBottom: 12,
      },
    });

    const frame = new ImageView({
      tag: 'item-bg',
      superview: view,
      image: 'assets/ui/decoration/frames/rectangle.png',
      width: 117,
      height: 117,
    });

    new ImageCoverView({
      superview: frame,
      image: getDecorationItemPath(item),
      mode: 'contain',
      verticalAlign: 'center',
      horizontalAlign: 'center',
      width: 100,
      height: 100,
      x: (frame.style.width - 100) * 0.5,
      y: (frame.style.height - 100) * 0.5,
    });

    const priceFrame = new ImageView({
      tag: 'item-price-bg',
      superview: view,
      image: 'assets/ui/decoration/frames/price.png',
      width: 117,
      height: 37,
    });

    new LangBitmapFontTextView({
      tag: 'item-price',
      superview: priceFrame,
      x: 14,
      y: 4,
      width: 58,
      height: 29,
      align: 'center',
      verticalAlign: 'center',
      size: 18,
      color: 'white',
      localeText: () => parseAmount(item.price),
      font: bitmapFonts('Body'),
    });

    new ImageView({
      tag: 'item-price-currency-icon',
      superview: priceFrame,
      image: this.getCurrencyIcon(item.currency),
      width: 38,
      height: 38,
      x: 82,
      y: 0,
    });

    return view;
  }

  /**
   * Create label for prices
   */
  private createLabel(
    superview: View,
    opts: { text: string; y: number; width: number },
  ) {
    const frame = new ImageScaleView({
      tag: 'frame',
      superview,
      x: (superview.style.width - 481) * 0.5,
      y: opts.y,
      width: opts.width,
      height: 47,
      image: 'assets/ui/decoration/frames/rectangle_light.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 43, right: 43 },
      },
    });

    new LangBitmapFontTextView({
      tag: 'label',
      superview: frame,
      x: 20,
      y: 0,
      width: 200,
      height: 47,
      align: 'left',
      verticalAlign: 'center',
      size: 24,
      color: '#BF8147',
      localeText: () => opts.text,
      font: bitmapFonts('Body'),
    });

    const priceView = new LangBitmapFontTextView({
      tag: 'price',
      superview: frame,
      x: opts.width - 260,
      y: 0,
      width: 200,
      height: 47,
      align: 'right',
      verticalAlign: 'center',
      size: 24,
      color: '#9A7171',
      localeText: () => '0',
      font: bitmapFonts('Body'),
    });

    const currencyIcon = new ImageView({
      tag: 'icon',
      superview: frame,
      width: 54,
      height: 54,
      x: opts.width - 48,
      y: 0,
    });

    return (currency: SupportedCurrency, price: number) => {
      priceView.color = price >= 0 ? '#9A7171' : '#cf0000';
      priceView.localeText = () => parseAmount(price);
      currencyIcon.setImage(this.getCurrencyIcon(currency));
    };
  }

  /**
   * Separator line
   */
  private createSeparatorLine(
    superview: View,
    opts: { y: number; width: number; height: number; color: string },
  ) {
    const { y, width, height, color } = opts;

    new View({
      superview,
      width,
      height,
      x: (superview.style.width - width) * 0.5,
      y,
      backgroundColor: color,
    });
  }

  /**
   * Return currency icon
   */
  private getCurrencyIcon(currency: 'bPoints' | 'gems') {
    switch (currency) {
      case 'gems':
        return 'assets/ui/hud/icons/hud_gem.png';
      case 'bPoints':
        return 'assets/ui/hud/icons/hud_coin.png';
      default:
        assertNever(currency);
    }
  }

  /**
   * Create cancel and save buttons
   */
  private createMainControls(superview: View, opts: { y: number }) {
    const container = new FlexBoxView({
      superview,
      width: superview.style.width,
      height: 120,
      x: 0,
      y: opts.y,
      flex: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 40,
      },
    });

    const cancel = new ButtonScaleViewWithText({
      superview: container,
      ...uiConfig.buttons.cancel,
      width: 235,
      height: 90,
      fontSize: 48,
      labelOffsetX: -6,
      labelOffsetY: -1,
      font: bitmapFonts('Body'),
      localeText: () => i18n('basic.back'),
      tracking: {
        feature: Feature.DECORATION._,
        $subFeature: Feature.DECORATION.PURCHASE_CONFIRMATION_POPUP_OK,
      },
      onClick: async () => {
        sounds.sfx.play('cancel');
        return this.close(false);
      },
    });

    const ok = new ButtonScaleViewWithText({
      superview: container,
      ...uiConfig.buttons.secondary,
      width: 235,
      height: 90,
      fontSize: 48,
      labelOffsetX: -6,
      labelOffsetY: -1,
      font: bitmapFonts('Body'),
      localeText: () => i18n('basic.ok'),
      tracking: {
        feature: Feature.DECORATION._,
        $subFeature: Feature.DECORATION.PURCHASE_CONFIRMATION_POPUP_OK,
      },
      onClick: async () => this.onConfirm(),
    });

    return { ok, cancel, container };
  }
}
